<template>
  <div class="image-similarity-page">
    <div class="hero-section">
      <div class="hero-content">
        <h1 class="page-title">造型相似度</h1>
        <p class="page-description">上传一张汽车图片，返回相似度最高的前二十张图</p>
        <div class="upload-card">
          <div
            class="upload-area"
            :class="{ 'is-drag-active': dragActive }"
            @click="triggerFilePicker"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              class="file-input"
              @change="handleFileChange"
            />
            <div v-if="previewUrl" class="preview-wrapper">
              <img :src="previewUrl" alt="预览图" class="preview-image" />
              <div class="preview-mask">
                <span>点击更换图片</span>
              </div>
            </div>
            <div v-else class="upload-placeholder">
              <div class="upload-icon">📷</div>
              <div class="upload-text">点击上传图片</div>
              <div class="upload-hint">支持 JPG/PNG/WebP，最大 50MB，可拖拽上传</div>
            </div>
          </div>
          <div class="upload-actions">
            <button class="secondary-btn" @click="triggerFilePicker">选择图片</button>
            <button class="primary-btn" :disabled="!selectedFile || loading" @click="performSearch">
              <span v-if="!loading">开始匹配</span>
              <span v-else class="btn-loading">
                <span class="loading-spinner"></span>
                <span>匹配中...</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="results-section">
      <div class="results-container">
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner-large"></div>
          <p>正在向量化并检索相似图片...</p>
        </div>

        <div v-else-if="results.length > 0" class="results-content">
          <div class="results-header">
            <div>
              <h2>相似度结果</h2>
              <p>已返回 {{ results.length }} 张图片</p>
            </div>
            <div class="results-actions">
              <button
                class="secondary-btn"
                :disabled="!canGenerateShare || shareGenerating"
                @click="generateShareImage"
              >
                <span v-if="!shareGenerating">生成分享图</span>
                <span v-else class="loading-spinner"></span>
              </button>
              <button
                v-if="shareImageUrl"
                class="secondary-btn"
                @click="downloadShareImage"
              >
                下载分享图
              </button>
            </div>
          </div>
          <div class="stats-row">
            <div class="stat-card">
              <div class="stat-label">最高匹配度</div>
              <div class="stat-value">{{ formatScore(topScore) }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">平均匹配度</div>
              <div class="stat-value">{{ formatScore(averageScore) }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">检索数量</div>
              <div class="stat-value">{{ results.length }}</div>
            </div>
          </div>
          <div v-if="shareImageUrl" class="share-preview-card">
            <div class="share-preview-header">
              <span>分享图预览</span>
              <span class="share-preview-tip">右键或点击按钮可保存</span>
            </div>
            <img :src="shareImageUrl" alt="分享图" />
          </div>
          <div class="image-grid">
            <div
              v-for="(image, index) in results"
              :key="image.id"
              class="image-item"
              @click="openImageModal(image)"
            >
              <div class="image-wrapper">
                <div class="rank-badge">TOP {{ index + 1 }}</div>
                <img
                  :src="getThumbnailUrl(image)"
                  :alt="image.title || '相似图片'"
                  loading="lazy"
                />
                <div class="image-overlay">
                  <div class="overlay-title">{{ image.model?.name || '未知车型' }}</div>
                  <div class="overlay-score">匹配度 {{ formatScore(image.vectorScore) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="hasSearched" class="empty-state">
          <p>未找到相似结果，请换一张图片试试</p>
        </div>
      </div>
    </div>

    <transition name="modal-fade">
      <div v-if="showModal" class="modal-backdrop" @click.self="showModal = false">
        <div class="modal-container">
          <button class="modal-close" @click="showModal = false">×</button>
          <div v-if="selectedImage" class="modal-content">
            <div class="modal-image-section">
              <img :src="getViewerUrl(selectedImage)" :alt="selectedImage.title || '图片'" />
            </div>
            <div class="modal-info-section">
              <h3>{{ selectedImage.model?.name || '未知车型' }}</h3>
              <p class="modal-brand">{{ selectedImage.brand?.name || '' }}</p>
              <div class="modal-score">匹配度：{{ formatScore(selectedImage.vectorScore) }}</div>
              <button
                v-if="selectedImage.modelId"
                class="secondary-btn"
                @click="goToModelDetail"
              >
                查看该车型图片
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { apiClient } from '@/services/api'

const MAX_UPLOAD_FILE_SIZE = 50 * 1024 * 1024

export default {
  name: 'ImageSimilarity',
  data() {
    return {
      selectedFile: null,
      previewUrl: '',
      results: [],
      loading: false,
      hasSearched: false,
      showModal: false,
      selectedImage: null,
      shareImageUrl: '',
      shareGenerating: false,
      dragActive: false
    }
  },
  computed: {
    canGenerateShare() {
      return Boolean(this.previewUrl && this.results.length >= 2 && !this.loading)
    },
    topScore() {
      if (!this.results.length) {
        return null
      }
      const first = this.results[0]
      return typeof first.vectorScore === 'number' ? first.vectorScore : null
    },
    averageScore() {
      if (!this.results.length) {
        return null
      }
      const validScores = this.results
        .map(item => item.vectorScore)
        .filter(score => typeof score === 'number')
      if (!validScores.length) {
        return null
      }
      const sum = validScores.reduce((acc, cur) => acc + cur, 0)
      return sum / validScores.length
    }
  },
  beforeDestroy() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
    }
  },
  methods: {
    triggerFilePicker() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    handleFileChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        return
      }
      this.setSelectedFile(file)
    },
    onDragOver() {
      this.dragActive = true
    },
    onDragLeave() {
      this.dragActive = false
    },
    onDrop(event) {
      this.dragActive = false
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]
      if (!file) {
        return
      }
      this.setSelectedFile(file)
    },
    setSelectedFile(file) {
      if (!file.type || !file.type.startsWith('image/')) {
        this.$message && this.$message.warning('请上传图片格式文件')
        return
      }
      if (file.size > MAX_UPLOAD_FILE_SIZE) {
        this.$message && this.$message.warning('图片过大，请选择 50MB 以内的图片')
        return
      }
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.selectedFile = file
      this.previewUrl = URL.createObjectURL(file)
      this.shareImageUrl = ''
    },
    async performSearch() {
      if (!this.selectedFile) {
        this.$message && this.$message.warning('请先选择图片')
        return
      }
      this.loading = true
      this.hasSearched = true
      this.results = []
      this.shareImageUrl = ''
      try {
        const formData = new FormData()
        formData.append('image', this.selectedFile)
        formData.append('limit', 20)

        const response = await apiClient.post('/image-similarity/search', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        if (response.status === 'success') {
          this.results = response.data.images || []
        } else {
          throw new Error(response.message || '检索失败')
        }
      } catch (error) {
        console.error('相似度检索失败:', error)
        this.$message && this.$message.error('检索失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    getThumbnailUrl(image) {
      if (!image) {
        return ''
      }
      if (image.Assets && Array.isArray(image.Assets) && image.Assets.length > 0) {
        const thumb = image.Assets.find(asset => asset.variant === 'thumbnail' || asset.variant === 'thumb')
        if (thumb && thumb.url) {
          return thumb.url
        }
        const small = image.Assets.find(asset => asset.variant === 'small')
        if (small && small.url) {
          return small.url
        }
        const medium = image.Assets.find(asset => asset.variant === 'medium')
        if (medium && medium.url) {
          return medium.url
        }
      }
      if (image.thumbnailUrl) {
        return image.thumbnailUrl
      }
      return image.bestUrl || image.url || ''
    },
    formatScore(score) {
      if (!score && score !== 0) {
        return '--'
      }
      return `${(score * 100).toFixed(1)}%`
    },
    getShareSource(image) {
      if (!image) {
        return ''
      }
      if (image.Assets && Array.isArray(image.Assets) && image.Assets.length > 0) {
        const large = image.Assets.find(asset => asset.variant === 'large')
        if (large && large.url) {
          return large.url
        }
        const medium = image.Assets.find(asset => asset.variant === 'medium')
        if (medium && medium.url) {
          return medium.url
        }
        const small = image.Assets.find(asset => asset.variant === 'small')
        if (small && small.url) {
          return small.url
        }
        const thumb = image.Assets.find(asset => asset.variant === 'thumbnail' || asset.variant === 'thumb')
        if (thumb && thumb.url) {
          return thumb.url
        }
      }
      return image.bestUrl || image.url || image.thumbnailUrl || ''
    },
    getViewerUrl(image) {
      if (!image) {
        return ''
      }
      if (image.Assets && Array.isArray(image.Assets) && image.Assets.length > 0) {
        const large = image.Assets.find(asset => asset.variant === 'large')
        if (large && large.url) {
          return large.url
        }
        const medium = image.Assets.find(asset => asset.variant === 'medium')
        if (medium && medium.url) {
          return medium.url
        }
        const webp = image.Assets.find(asset => asset.variant === 'webp')
        if (webp && webp.url) {
          return webp.url
        }
      }
      return image.displayUrl || image.bestUrl || image.url || image.thumbnailUrl || this.getThumbnailUrl(image)
    },
    buildShareProxyUrl(url) {
      if (!url) {
        return ''
      }
      if (url.startsWith('blob:') || url.startsWith('data:')) {
        return url
      }
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return `/api/image-similarity/share-image?url=${encodeURIComponent(url)}`
      }
      return url
    },
    normalizeWeights(firstScore, secondScore) {
      const safeFirst = typeof firstScore === 'number' ? Math.max(firstScore, 0) : 0.5
      const safeSecond = typeof secondScore === 'number' ? Math.max(secondScore, 0) : 0.5
      const sum = safeFirst + safeSecond
      if (!sum) {
        return [0.5, 0.5]
      }
      return [safeFirst / sum, safeSecond / sum]
    },
    loadImage(url, useCrossOrigin = true) {
      return new Promise((resolve, reject) => {
        if (!url) {
          reject(new Error('图片地址为空'))
          return
        }
        const img = new Image()
        if (useCrossOrigin) {
          img.crossOrigin = 'anonymous'
        }
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('图片加载失败'))
        img.src = url
      })
    },
    drawImageContain(ctx, img, x, y, w, h) {
      const scale = Math.min(w / img.width, h / img.height)
      const drawW = img.width * scale
      const drawH = img.height * scale
      const drawX = x + (w - drawW) / 2
      const drawY = y + (h - drawH) / 2
      ctx.drawImage(img, drawX, drawY, drawW, drawH)
    },
    async generateShareImage() {
      if (!this.canGenerateShare) {
        this.$message && this.$message.warning('请先完成检索并确保至少两张结果')
        return
      }
      this.shareGenerating = true
      try {
        const firstImage = this.results[0]
        const secondImage = this.results[1]
        const [weight1, weight2] = this.normalizeWeights(firstImage.vectorScore, secondImage.vectorScore)
        const percent1 = (weight1 * 100).toFixed(1)
        const percent2 = (weight2 * 100).toFixed(1)

        const [img1, img2, uploaded] = await Promise.all([
          this.loadImage(this.buildShareProxyUrl(this.getShareSource(firstImage))),
          this.loadImage(this.buildShareProxyUrl(this.getShareSource(secondImage))),
          this.loadImage(this.previewUrl, false)
        ])

        const canvas = document.createElement('canvas')
        const width = 1200
        const height = 800
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#0b0b0b'
        ctx.fillRect(0, 0, width, height)

        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
        ctx.font = 'bold 32px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText('相似度分享', width / 2, 60)

        ctx.fillStyle = 'rgba(255, 255, 255, 0.65)'
        ctx.font = '16px sans-serif'
        ctx.fillText(`图1 × ${percent1}% + 图2 × ${percent2}% = 上传图`, width / 2, 92)

        const boxW = 300
        const boxH = 180
        const symbolW = 40
        const gap = 40
        const totalW = boxW * 3 + gap * 2 + symbolW * 2
        const startX = (width - totalW) / 2
        const topY = 140

        const box1X = startX
        const plusX = box1X + boxW + gap / 2
        const box2X = box1X + boxW + gap + symbolW
        const equalX = box2X + boxW + gap / 2
        const box3X = box2X + boxW + gap + symbolW

        ctx.fillStyle = 'rgba(255, 255, 255, 0.12)'
        ctx.fillRect(box1X - 8, topY - 8, boxW + 16, boxH + 16)
        ctx.fillRect(box2X - 8, topY - 8, boxW + 16, boxH + 16)
        ctx.fillRect(box3X - 8, topY - 8, boxW + 16, boxH + 16)

        ctx.fillStyle = '#111'
        ctx.fillRect(box1X, topY, boxW, boxH)
        ctx.fillRect(box2X, topY, boxW, boxH)
        ctx.fillRect(box3X, topY, boxW, boxH)

        this.drawImageContain(ctx, img1, box1X, topY, boxW, boxH)
        this.drawImageContain(ctx, img2, box2X, topY, boxW, boxH)
        this.drawImageContain(ctx, uploaded, box3X, topY, boxW, boxH)

        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
        ctx.font = '28px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText('+', plusX + symbolW / 2, topY + boxH / 2 + 10)
        ctx.fillText('=', equalX + symbolW / 2, topY + boxH / 2 + 10)

        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
        ctx.font = '14px sans-serif'
        ctx.fillText(`图1 · ${percent1}%`, box1X + boxW / 2, topY + boxH + 28)
        ctx.fillText(`图2 · ${percent2}%`, box2X + boxW / 2, topY + boxH + 28)
        ctx.fillText('上传图', box3X + boxW / 2, topY + boxH + 28)

        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
        ctx.font = '13px sans-serif'
        ctx.textAlign = 'left'
        ctx.fillText(`图1：${firstImage.model?.name || '未知车型'}`, 120, 400)
        ctx.fillText(`图2：${secondImage.model?.name || '未知车型'}`, 120, 422)

        ctx.fillStyle = 'rgba(255, 255, 255, 0.35)'
        ctx.font = '12px sans-serif'
        ctx.fillText(`生成时间：${new Date().toLocaleString()}`, 120, 460)

        this.shareImageUrl = canvas.toDataURL('image/png')
      } catch (error) {
        console.error('生成分享图失败:', error)
        this.$message && this.$message.error('生成分享图失败，请稍后重试')
      } finally {
        this.shareGenerating = false
      }
    },
    downloadShareImage() {
      if (!this.shareImageUrl) {
        return
      }
      const link = document.createElement('a')
      link.href = this.shareImageUrl
      link.download = 'image-similarity-share.png'
      link.click()
    },
    openImageModal(image) {
      this.selectedImage = image
      this.showModal = true
    },
    goToModelDetail() {
      if (this.selectedImage && this.selectedImage.modelId) {
        this.showModal = false
        this.$router.push(`/model/${this.selectedImage.modelId}`)
      }
    }
  }
}
</script>

<style scoped>
.image-similarity-page {
  min-height: 100vh;
  background: #0a0a0a;
  color: rgba(255, 255, 255, 0.9);
}

.hero-section {
  padding: 90px 20px 40px;
}

.hero-content {
  max-width: 960px;
  margin: 0 auto;
  text-align: center;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 12px;
}

.page-description {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.7);
}

.upload-card {
  margin-top: 32px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.25);
}

.upload-area {
  position: relative;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.upload-area:hover {
  border-color: rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.02);
}

.upload-area.is-drag-active {
  border-color: rgba(255, 71, 87, 0.7);
  background: rgba(255, 71, 87, 0.08);
  transform: scale(1.005);
}

.file-input {
  display: none;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upload-icon {
  font-size: 32px;
}

.upload-text {
  font-size: 16px;
  font-weight: 600;
}

.upload-hint {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
}

.preview-wrapper {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  display: block;
}

.preview-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity 0.2s ease;
  font-size: 14px;
}

.preview-wrapper:hover .preview-mask {
  opacity: 1;
}

.upload-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}

.primary-btn,
.secondary-btn {
  border: none;
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.primary-btn {
  background: linear-gradient(135deg, #e03426 0%, #ff4757 100%);
  color: white;
  min-width: 126px;
}

.primary-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.secondary-btn {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
}

.btn-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.loading-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.results-section {
  padding: 0 20px 60px;
}

.results-container {
  max-width: 1400px;
  margin: 0 auto;
}

.loading-state {
  text-align: center;
  padding: 60px 0;
}

.loading-spinner-large {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255, 255, 255, 0.15);
  border-top-color: #e03426;
  border-radius: 50%;
  margin: 0 auto 16px;
  animation: spin 0.8s linear infinite;
}

.results-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;
}

.results-header p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.results-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.stat-card {
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  padding: 12px 14px;
}

.stat-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.62);
}

.stat-value {
  margin-top: 6px;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

.share-preview-card {
  margin-bottom: 20px;
  padding: 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.share-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
}

.share-preview-tip {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.share-preview-card img {
  width: 100%;
  border-radius: 10px;
  display: block;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}

.image-wrapper {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.image-wrapper:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.35);
  border-color: rgba(255, 255, 255, 0.2);
}

.rank-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
}

.image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.image-wrapper:hover img {
  transform: scale(1.04);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.85) 100%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 12px;
  opacity: 0.24;
  transition: opacity 0.2s ease;
}

.image-wrapper:hover .image-overlay {
  opacity: 1;
}

.overlay-title {
  font-size: 14px;
  font-weight: 600;
}

.overlay-score {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.empty-state {
  text-align: center;
  padding: 80px 0;
  color: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  border: 1px dashed rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.02);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-container {
  background: rgba(15, 15, 15, 0.95);
  border-radius: 16px;
  max-width: 1100px;
  width: 90%;
  overflow: hidden;
  position: relative;
}

.modal-close {
  position: absolute;
  right: 16px;
  top: 12px;
  background: transparent;
  color: white;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.modal-content {
  display: flex;
  gap: 24px;
  padding: 24px;
}

.modal-image-section {
  flex: 1.4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-image-section img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.modal-info-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-brand {
  color: rgba(255, 255, 255, 0.7);
}

.modal-score {
  font-weight: 600;
  color: #ff6b6b;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 1200px) {
  .image-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 900px) {
  .image-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .stats-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .modal-content {
    flex-direction: column;
  }
}

@media (max-width: 600px) {
  .image-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .upload-actions {
    flex-direction: column;
  }

  .stats-row {
    grid-template-columns: 1fr;
  }

  .results-actions {
    width: 100%;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

}
</style>

